import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ShoppingCart,
  ShoppingBag,
  ArrowLeft,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Share2,
  ChevronRight as ChevronRightSmall,
} from 'lucide-react';
import { useSelector } from 'react-redux';
import { getCourier } from '../../state/courierSlice';
import { apiConfig } from '../../shared/api-config';
import { showErrorToast, showSuccessToast } from '../../utils/toast';
import { useCart } from '../../context/CartContext';
import { useDarkMode } from '../../context/DarkModeContext';
import { ShopProductApi, ShopProduct as ShopProductModel } from '@shared/sdk-v1';
import type { SelectedOption } from '../../context/CartContext';

const ProductDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isDarkMode } = useDarkMode();
  const courier = useSelector(getCourier);
  const { cartItems, addToCart, updateQuantity, removeFromCart } = useCart();

  const [product, setProduct] = useState<ShopProductModel | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedOptions, setSelectedOptions] = useState<Record<number, string[]>>({});
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const fetchProduct = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const api = new ShopProductApi(apiConfig());
      const data = await api.shopProductFindById({
        id,
        filter: JSON.stringify({
          include: [{ relation: 'options', scope: { include: ['choices'], order: 'sortOrder ASC' } }],
        }),
      });
      setProduct(data);
    } catch (error) {
      console.error('Error fetching product:', error);
      showErrorToast({ title: 'Error', message: 'Product not found' });
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchProduct();
  }, [fetchProduct]);

  // Update page title and meta tags for SEO
  useEffect(() => {
    if (!product) return;

    const courierName = courier?.name || 'Shop';
    const title = (product as any).metaTitle || product.name || 'Product';
    const description = (product as any).metaDescription
      || (product.description ? product.description.substring(0, 160) : `${title} — available at ${courierName}`);
    const keywords = (product as any).metaKeywords;
    const imageUrl = product.images?.[0]
      ? (product.images[0].startsWith('http') ? product.images[0] : `${import.meta.env.VITE_API_BASE_URL}/Uploads/${product.courierId}/download/${product.images[0]}`)
      : undefined;

    document.title = `${title} | ${courierName}`;

    const setMeta = (attr: string, key: string, content: string) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('name', 'description', description);
    if (keywords) setMeta('name', 'keywords', keywords);
    setMeta('property', 'og:type', 'product');
    setMeta('property', 'og:title', `${title} | ${courierName}`);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:url', window.location.href);
    if (imageUrl) setMeta('property', 'og:image', imageUrl);
    if (product.price) {
      setMeta('property', 'product:price:amount', String(product.price));
      setMeta(
        'property',
        'product:price:currency',
        courier?.courierSetting?.currency ??
          (courier?.courierSetting?.useJmd ? 'JMD' : 'USD')
      );
    }
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', `${title} | ${courierName}`);
    setMeta('name', 'twitter:description', description);
    if (imageUrl) setMeta('name', 'twitter:image', imageUrl);

    return () => {
      document.title = courierName;
      ['og:type', 'product:price:amount', 'product:price:currency'].forEach(key => {
        const el = document.querySelector(`meta[property="${key}"]`);
        if (el) el.remove();
      });
    };
  }, [product, courier?.name]);

  const getImageUrl = (img: string) => {
    if (img.startsWith('http')) return img;
    return `${import.meta.env.VITE_API_BASE_URL}/Uploads/${product?.courierId}/download/${img}`;
  };

  const getConditionBadge = (condition?: string) => {
    const baseClasses = 'inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium';
    switch (condition?.toLowerCase()) {
      case 'new': return `${baseClasses} bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200`;
      case 'like_new': return `${baseClasses} bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200`;
      case 'good': return `${baseClasses} bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200`;
      case 'fair': return `${baseClasses} bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200`;
      default: return `${baseClasses} bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200`;
    }
  };

  const handleAddToCart = () => {
    if (!product?.id || !product.name || !product.price) return;

    if (product.isConfigurable && product.options?.length) {
      for (const opt of product.options) {
        if (opt.required && (!selectedOptions[opt.id!] || selectedOptions[opt.id!].length === 0)) {
          showErrorToast({ title: 'Required', message: `Please select ${opt.name}` });
          return;
        }
      }

      const opts: SelectedOption[] = [];
      let totalModifier = 0;
      for (const opt of product.options) {
        const choiceIds = selectedOptions[opt.id!] || [];
        if (choiceIds.length === 0) continue;
        const choiceNames: string[] = [];
        let optModifier = 0;
        for (const chId of choiceIds) {
          const choice = opt.choices?.find(c => String(c.id) === chId);
          if (choice) {
            choiceNames.push(choice.name || '');
            optModifier += choice.priceModifier || 0;
          }
        }
        totalModifier += optModifier;
        opts.push({ optionName: opt.name || '', choiceNames, priceModifier: optModifier });
      }

      const finalPrice = (product.price || 0) + totalModifier;
      addToCart({
        productId: product.id,
        name: product.name,
        price: finalPrice,
        basePrice: product.price,
        image: product.images?.[0],
        condition: product.condition,
        selectedOptions: opts,
      });
    } else {
      const existingItem = cartItems.find(item => item.cartKey === String(product.id));
      if (existingItem) {
        updateQuantity(existingItem.cartKey, existingItem.quantity + 1);
      } else {
        addToCart({
          productId: product.id,
          name: product.name,
          price: product.price,
          basePrice: product.price,
          image: product.images?.[0],
          condition: product.condition,
        });
      }
    }
    showSuccessToast({ title: 'Added to cart', message: product.name || '' });
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: product?.name || 'Product', url });
      } catch { /* user cancelled */ }
    } else {
      await navigator.clipboard.writeText(url);
      showSuccessToast({ title: 'Link copied', message: 'Product link copied to clipboard' });
    }
  };

  const getTotalWithOptions = () => {
    if (!product?.price) return 0;
    let totalMod = 0;
    for (const opt of product.options || []) {
      for (const chId of (selectedOptions[opt.id!] || [])) {
        const ch = opt.choices?.find(c => String(c.id) === chId);
        totalMod += ch?.priceModifier || 0;
      }
    }
    return product.price + totalMod;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary dark:text-blue-400" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="text-center py-20">
        <ShoppingBag className="mx-auto h-16 w-16 text-gray-400 dark:text-gray-500" />
        <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">Product not found</h3>
        <button
          onClick={() => navigate('/shop')}
          className="mt-4 inline-flex items-center px-4 py-2 bg-primary text-white rounded-lg"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Shop
        </button>
      </div>
    );
  }

  const images = product.images || [];
  const quantity = cartItems.find(item => item.cartKey === String(product.id))?.quantity || 0;

  return (
    <div className="bg-gray-50 dark:bg-gray-900 min-h-full">
      <div className="max-w-7xl mx-auto">
        {/* Desktop Breadcrumbs */}
        <nav className="hidden lg:flex items-center gap-2 px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
          <Link to="/shop" className="hover:text-primary dark:hover:text-blue-400 transition-colors">Shop</Link>
          <ChevronRightSmall className="h-4 w-4" />
          {product.category && (
            <>
              <span className="hover:text-primary dark:hover:text-blue-400 cursor-default">{product.category}</span>
              <ChevronRightSmall className="h-4 w-4" />
            </>
          )}
          <span className="text-gray-900 dark:text-white font-medium truncate max-w-xs">{product.name}</span>
        </nav>

        {/* Desktop: Two-column layout / Mobile: Stacked */}
        <div className="lg:flex lg:gap-10 lg:px-6 lg:pb-10">
          {/* Image Gallery */}
          <div className="lg:w-[58%] lg:flex-shrink-0">
            <div className="relative bg-white dark:bg-gray-800 lg:rounded-xl lg:overflow-hidden lg:shadow-sm lg:border lg:border-gray-200 lg:dark:border-gray-700">
              {/* Back button overlay - mobile only */}
              <button
                onClick={() => navigate('/shop')}
                className="lg:hidden absolute top-4 left-4 z-10 p-2 bg-black/30 hover:bg-black/50 rounded-full text-white transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>

              {/* Share button overlay */}
              <button
                onClick={handleShare}
                className="absolute top-4 right-4 z-10 p-2 bg-black/30 hover:bg-black/50 rounded-full text-white transition-colors"
              >
                <Share2 className="h-5 w-5" />
              </button>

              {images.length > 0 ? (
                <div className="relative aspect-square max-h-[50vh] sm:max-h-[60vh] lg:max-h-none lg:aspect-[4/3]">
                  <img
                    src={getImageUrl(images[currentImageIndex])}
                    alt={product.name}
                    className="w-full h-full object-contain bg-gray-100 dark:bg-gray-700"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="400" viewBox="0 0 400 400"%3E%3Crect fill="%23ddd" width="400" height="400"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="30" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3ENo Image%3C/text%3E%3C/svg%3E';
                    }}
                  />
                  {images.length > 1 && (
                    <>
                      <button
                        onClick={() => setCurrentImageIndex(i => i === 0 ? images.length - 1 : i - 1)}
                        className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-black/30 hover:bg-black/50 rounded-full text-white"
                      >
                        <ChevronLeft className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => setCurrentImageIndex(i => i === images.length - 1 ? 0 : i + 1)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-black/30 hover:bg-black/50 rounded-full text-white"
                      >
                        <ChevronRight className="h-5 w-5" />
                      </button>
                      {/* Mobile Dots */}
                      <div className="lg:hidden absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                        {images.map((_, i) => (
                          <button
                            key={i}
                            onClick={() => setCurrentImageIndex(i)}
                            className={`w-2 h-2 rounded-full transition-colors ${
                              i === currentImageIndex ? 'bg-white' : 'bg-white/50'
                            }`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className="aspect-square max-h-[50vh] lg:max-h-none lg:aspect-[4/3] bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                  <ShoppingBag className="h-20 w-20 text-gray-400 dark:text-gray-500" />
                </div>
              )}
            </div>

            {/* Desktop Thumbnail Strip */}
            {images.length > 1 && (
              <div className="hidden lg:flex gap-2 mt-3">
                {images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentImageIndex(i)}
                    className={`w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors flex-shrink-0
                      ${i === currentImageIndex
                        ? 'border-primary'
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                      }`}
                  >
                    <img
                      src={getImageUrl(img)}
                      alt={`${product.name} ${i + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 80 80"%3E%3Crect fill="%23ddd" width="80" height="80"/%3E%3C/svg%3E';
                      }}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="lg:w-[42%] lg:flex-shrink-0">
            <div className="p-4 lg:p-0 lg:pt-2 space-y-4 lg:space-y-6">
              {/* Name + Condition */}
              <div>
                <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">{product.name}</h1>
                <div className="flex items-center gap-2 mt-2">
                  {product.condition && (
                    <span className={getConditionBadge(product.condition)}>
                      {product.condition.replace('_', ' ')}
                    </span>
                  )}
                  {product.category && (
                    <span className="text-sm text-gray-500 dark:text-gray-400">{product.category}</span>
                  )}
                </div>
              </div>

              {/* Price */}
              <div className="flex items-baseline gap-3">
                <span className="text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white">
                  JMD ${(product.isConfigurable ? getTotalWithOptions() : product.price)?.toFixed(2) || '0.00'}
                </span>
                {product.originalPrice && product.originalPrice > (product.price || 0) && (
                  <span className="text-lg text-gray-500 dark:text-gray-400 line-through">
                    ${product.originalPrice.toFixed(2)}
                  </span>
                )}
              </div>

              {/* Availability */}
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {product.quantity && product.quantity > 0
                  ? `${product.quantity} available`
                  : 'Out of stock'}
              </p>

              {/* Configurable Options */}
              {product.isConfigurable && product.options && product.options.length > 0 && (
                <div className="pt-4 border-t dark:border-gray-700 space-y-4">
                  {product.options.map((opt) => (
                    <div key={opt.id}>
                      <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                        {opt.name}
                        {opt.required && <span className="text-red-500 ml-1">*</span>}
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {opt.choices?.map((choice) => {
                          const isSelected = (selectedOptions[opt.id!] || []).includes(String(choice.id));
                          return (
                            <button
                              key={choice.id}
                              onClick={() => {
                                setSelectedOptions(prev => {
                                  const current = prev[opt.id!] || [];
                                  if (opt.type === 'single') {
                                    return { ...prev, [opt.id!]: isSelected ? [] : [String(choice.id)] };
                                  } else {
                                    return {
                                      ...prev,
                                      [opt.id!]: isSelected
                                        ? current.filter(cid => cid !== String(choice.id))
                                        : [...current, String(choice.id)]
                                    };
                                  }
                                });
                              }}
                              className={`px-4 py-2.5 rounded-lg text-sm font-medium border transition-colors min-h-[44px]
                                ${isSelected
                                  ? 'bg-primary text-white border-primary'
                                  : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:border-primary'
                                }`}
                            >
                              {choice.name}
                              {(choice.priceModifier || 0) > 0 && (
                                <span className="ml-1 opacity-75">+${choice.priceModifier?.toFixed(2)}</span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                      {opt.type === 'multi' && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Select one or more</p>
                      )}
                    </div>
                  ))}

                  {/* Price breakdown */}
                  {(() => {
                    let totalMod = 0;
                    for (const opt of product.options || []) {
                      for (const chId of (selectedOptions[opt.id!] || [])) {
                        const ch = opt.choices?.find(c => String(c.id) === chId);
                        totalMod += ch?.priceModifier || 0;
                      }
                    }
                    return totalMod > 0 ? (
                      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 mt-2">
                        <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400">
                          <span>Base price</span>
                          <span>JMD ${product.price?.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400">
                          <span>Options</span>
                          <span>+${totalMod.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between font-bold text-gray-900 dark:text-white border-t dark:border-gray-700 mt-2 pt-2">
                          <span>Total</span>
                          <span>JMD ${((product.price || 0) + totalMod).toFixed(2)}</span>
                        </div>
                      </div>
                    ) : null;
                  })()}
                </div>
              )}

              {/* Description */}
              {product.description && (
                <div className="pt-4 border-t dark:border-gray-700">
                  <h2 className="font-semibold text-gray-900 dark:text-white mb-2">Description</h2>
                  <p className="text-gray-600 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
                    {product.description}
                  </p>
                </div>
              )}

              {/* Desktop Add to Cart - inline */}
              <div className="hidden lg:block pt-4">
                {product.quantity && product.quantity > 0 ? (
                  <button
                    onClick={handleAddToCart}
                    className="w-full bg-primary hover:bg-primary/90 text-white py-4 rounded-xl
                      font-medium text-lg flex items-center justify-center min-h-[44px] transition-colors shadow-sm"
                  >
                    <ShoppingCart className="h-5 w-5 mr-2" />
                    Add to Cart
                    {quantity > 0 && !product.isConfigurable && (
                      <span className="ml-2 bg-white/20 rounded-full px-2 py-0.5 text-sm">
                        {quantity} in cart
                      </span>
                    )}
                  </button>
                ) : (
                  <button
                    disabled
                    className="w-full bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400
                      py-4 rounded-xl font-medium text-lg cursor-not-allowed min-h-[44px]"
                  >
                    Sold Out
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Sticky Add to Cart Bar */}
        <div className="lg:hidden sticky bottom-0 p-4 bg-white dark:bg-gray-800 border-t dark:border-gray-700 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]">
          {product.quantity && product.quantity > 0 ? (
            <button
              onClick={handleAddToCart}
              className="w-full bg-primary hover:bg-primary/90 text-white py-4 rounded-lg
                font-medium text-lg flex items-center justify-center min-h-[44px] transition-colors"
            >
              <ShoppingCart className="h-5 w-5 mr-2" />
              Add to Cart
              {quantity > 0 && !product.isConfigurable && (
                <span className="ml-2 bg-white/20 rounded-full px-2 py-0.5 text-sm">
                  {quantity} in cart
                </span>
              )}
            </button>
          ) : (
            <button
              disabled
              className="w-full bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400
                py-4 rounded-lg font-medium text-lg cursor-not-allowed min-h-[44px]"
            >
              Sold Out
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
